import { call, put } from "redux-saga/effects";

import * as api from "../../api/TdsApi";
import * as types from "../../constants/actionTypes";
import { createErrorMessageAction } from "../../utils/TypescriptUtils";
import { cookieManager } from "../../utils/Utils";
import { uploadLookupFileSuccess, uploadTemplateSuccess } from "../actions/NewTopologyActions";

export function* getTopologyTypesSaga(action) {
  try {
    let topologyTypes = [];

    let errors = "";

    for (var i = 0; i < action.environments.length; i++) {
      let topos = [];
      let environment = action.environments[i];
      try {
        topos = yield call(api.getTopologyTypes, environment);
      } catch (error) {
        errors += `Unable to retrieve topologies for environment ${environment.display}. Error ${error}\n`;
      }
      // Add environment to each topology type object, add to topologyTypes
      for (var j = 0; j < topos.length; j++) {
        topos[j].environment = environment;
        const topology = topos[j];

        const savedRegionId = Number(cookieManager.getCookie(topology.DisplayName)) || null;
        const enabledRegion = topology.TopologyManagements.filter((management) => management.Enabled).map(
          (management) => (management.Regions.Id !== null ? management.Regions.Id : -1),
        );

        if (enabledRegion.length > 0) {
          // Check for saved region first (highest priority)
          if (savedRegionId && enabledRegion.includes(savedRegionId)) {
            topology.RequestRegionId = savedRegionId;
          }
          // Otherwise, set to null to allow auto-prepopulation to work
          else {
            topology.RequestRegionId = null;
          }
        } topologyTypes.push(topos[j]);
      }
    }

    yield put({ type: types.FETCH_TOPOLOGY_TYPES_SUCCESS, topologyTypes });

    if (errors.length > 0) {
      throw new Error(errors);
    }
  } catch (error) {
    yield put(createErrorMessageAction(types.FETCH_TOPOLOGY_TYPES_ERROR, error));
  }
}

export function* getCloudTypesDefaultClassicSaga(action) {
  try {
    const cloudTypesDefaultClassic = yield call(api.getGetCloudTypesDefaultClassic, action.environment);
    yield put({ type: types.FETCH_CLOUD_TYPES_DEFAULT_CLASSIC_SUCCESS, cloudTypesDefaultClassic });
  } catch (error) {
    yield put(createErrorMessageAction(types.FETCH_CLOUD_TYPES_DEFAULT_CLASSIC_ERROR, error));
  }
}

export function* checkoutTopologySaga(action) {
  try {
    const response = yield call(
      api.checkoutTopology,
      action.topologyName,
      action.owner,
      action.topologyType,
      action.buildNumber,
      action.environment,
    );
    yield put({ type: types.CHECKOUT_SUCCESS, response });

    if (window.location) {
      window.location.pathname = "/";
    }
  } catch (error) {
    yield put(createErrorMessageAction(types.CHECKOUT_ERROR, error));
  }
}

export function* deployTopologyTypeWithQueueSaga(action) {
  try {
    const response = yield call(api.deployTopologyTypeWithQueue, action.args, action.environment);
    yield put({ type: types.DEPLOY_TOPOLOGY_TYPE_WITH_QUEUE_SUCCESS, response });

    if (window.location) {
      window.location.pathname = "/";
    }
  } catch (error) {
    yield put(createErrorMessageAction(types.DEPLOY_TOPOLOGY_TYPE_WITH_QUEUE_ERROR, error));
  }
}

export function* uploadTemplateSaga(action) {
  try {
    const template = yield call(
      api.uploadTemplate,
      action.partnerId,
      action.user,
      action.fileName,
      action.fileContent,
      action.environment,
    );
    yield put(uploadTemplateSuccess(template, action.partnerId, action.environment));
  } catch (error) {
    yield put(createErrorMessageAction(types.UPLOAD_TEMPLATE_ERROR, error));
  }
}

export function* uploadTemplateForLookupFileSaga(action) {
  try {
    const template = yield call(
      api.uploadTemplate,
      action.partnerId,
      action.user,
      action.fileName,
      action.fileContent,
      action.environment,
    );
    const returnValue = {
      template: template,
      partnerId: action.partnerId,
      environment: action.environment,
    };
    yield put({ type: types.UPLOAD_TEMPLATE_FOR_LOOKUP_FILE_SUCCESS, returnValue });
  } catch (error) {
    yield put(createErrorMessageAction(types.UPLOAD_TEMPLATE_FOR_LOOKUP_FILE_ERROR, error.toString()));
  }
}

export function* uploadLookupFileSaga(action) {
  try {
    const template = yield call(
      api.uploadLookupFile,
      action.partnerId,
      action.user,
      action.fileName,
      action.fileContent,
      action.templateUri,
      action.environment,
    );
    yield put(uploadLookupFileSuccess(template, action.partnerId, action.environment));
  } catch (error) {
    yield put(createErrorMessageAction(types.UPLOAD_LOOKUPFILE_ERROR, error));
  }
}

export function* deployPrivateTemplateSaga(action) {
  try {
    const response = yield call(
      api.deployPrivateTemplate,
      action.topologyName,
      action.owner,
      action.path,
      action.userData,
      action.environment,
    );
    yield put({ type: types.DEPLOY_PRIVATE_TEMPLATE_SUCCESS, response });

    if (window.location) {
      window.location.pathname = "/";
    }
  } catch (error) {
    yield put(createErrorMessageAction(types.DEPLOY_PRIVATE_TEMPLATE_ERROR, error));
  }
}

export function* deployLookupTopologyTypeSaga(action) {
  try {
    const response = yield call(api.deployLookupTopologyType, action.topologyTypeArgs, action.environment);
    yield put({ type: types.DEPLOY_LOOKUP_TOPOLOGY_TYPE_SUCCESS, response });

    if (window.location) {
      window.location.pathname = "/";
    }
  } catch (error) {
    yield put(createErrorMessageAction(types.DEPLOY_LOOKUP_TOPOLOGY_TYPE_ERROR, error));
  }
}

export function* getDuplicateThresholdTopologiesSage(action) {
  try {
    const duplicateThresholdTopologies = yield call(api.getDuplicateThresholdTopologies, action.environment);
    yield put({ type: types.FETCH_DUPLICATE_THRESHOLD_TOPOLOGY_NAMES_SUCCESS, duplicateThresholdTopologies });
  } catch (error) {
    yield put(createErrorMessageAction(types.FETCH_DUPLICATE_THRESHOLD_TOPOLOGY_NAMES_ERROR, error));
  }
}

export function* getUserRegionSaga(action) {
  try {
    const userRegion = yield call(api.getUserRegion, action.environment);

    // Check if it's an error result object
    if (userRegion && userRegion.success === false) {
      // Dispatch error action but don't create global error banner
      yield put({
        type: types.GET_USER_REGION_ERROR,
        error: userRegion.error,
        responseBody: userRegion.responseBody
      });
    } else if (userRegion && userRegion.success === true) {
      // Successful structured response
      yield put({ type: types.GET_USER_REGION_SUCCESS, userRegion: userRegion.data });
    } else if (userRegion === null) {
      // User not enabled for feature, silently handle
      yield put({ type: types.GET_USER_REGION_SUCCESS, userRegion: null });
    } else {
      // Unexpected response shape — treat as an error to avoid silently accepting malformed data
      yield put({
        type: types.GET_USER_REGION_ERROR,
        error: "Unexpected response format from getUserRegion API.",
        responseBody: JSON.stringify(userRegion)
      });
    }
  } catch (error) {
    yield put({
      type: types.GET_USER_REGION_ERROR,
      error: error.message || "Unexpected error"
    });
  }
}
